'use client';

import { AUTH_PROVIDER, SESSION_STATUS } from '@/constants/auth';
import { PropsWithChildren, useEffect } from 'react';
import { useSession, signIn } from 'next-auth/react';
import { usePathname } from 'next/navigation';
import { PRIVATE_TOP_LEVEL_SEGMENTS } from '@/configs/routes';
import { Shell } from '../shell';
import { Private } from './Private';
import { PrivateStateProvider } from './PrivateStateProvider';
import { LoadingPage } from '@/components/utils';

export function isPrivatePath(pathname: string): boolean {
  const segments = pathname.split('/').filter(Boolean);
  if (
    segments.length >= 1 &&
    PRIVATE_TOP_LEVEL_SEGMENTS.includes(segments[0]!)
  ) {
    return true;
  }
  // /{orgName}/registry/...
  return segments.length >= 2 && segments[1] === 'registry';
}

export default function PrivateGate({ children }: PropsWithChildren) {
  const { status } = useSession();
  const pathname = usePathname();
  const onPrivatePath = isPrivatePath(pathname);

  useEffect(() => {
    if (status === SESSION_STATUS.UNAUTHENTICATED && onPrivatePath) {
      void signIn(AUTH_PROVIDER, {
        callbackUrl: pathname + window.location.search,
      });
    }
  }, [status, onPrivatePath, pathname]);

  if (
    (status === SESSION_STATUS.LOADING ||
      status === SESSION_STATUS.UNAUTHENTICATED) &&
    onPrivatePath
  ) {
    return <LoadingPage height="100vh" />;
  }

  // `PrivateStateProvider` owns the lifted `PrivateContext` state and wraps
  // `<Shell>` so the navbar's user-aware bits keep reading from the same
  // context instance regardless of auth status. `<Private>` mounts
  // unconditionally and self-gates its auth-dependent work on `useSession()`,
  // keeping the route subtree's tree position stable across the LOADING →
  // AUTHENTICATED transition so it does not remount (and lose SWR cache +
  // local state) when the session resolves.
  return (
    <PrivateStateProvider>
      <Shell>
        <Private>{children}</Private>
      </Shell>
    </PrivateStateProvider>
  );
}
